import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { not } from 'ember-truth-helpers';
import TranslatableContent from 'garaje/components/visitors/questionnaires/form/translatable-content';
import type QuestionnaireFieldOption from 'garaje/models/questionnaire-field-option';
import type { LocaleKey } from 'garaje/utils/locale-options';
import type QuestionnaireValidator from 'garaje/utils/questionnaire-validator';
import { cached } from 'tracked-toolbox';

interface QuestionOptionSignature {
  Args: {
    defaultLocale: LocaleKey;
    deleteOption: (option: QuestionnaireFieldOption) => void;
    inputId: string;
    isDraggable: boolean;
    isMultipleSelection: boolean;
    isRTL?: boolean;
    modifyOption: (option: QuestionnaireFieldOption, newValue: string) => void;
    option: QuestionnaireFieldOption;
    readOnly?: boolean;
    selectedLocale: LocaleKey;
    setCorrectAnswer: (option: QuestionnaireFieldOption) => void;
    showDeleteOptionButton: boolean;
    validator: QuestionnaireValidator;
  };
  Element: HTMLLIElement;
}

function preventDefaultOnEnter(event: KeyboardEvent): boolean {
  let bubbleUp = true;

  if (event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    bubbleUp = false;
  }

  return bubbleUp;
}

export default class QuestionOption extends Component<QuestionOptionSignature> {
  get canMarkCorrectAnswer(): boolean {
    // The user can't choose/change the correct answer(s) if:
    // 1) we're read-only, or
    // 2) the user is viewing a non-default language
    if (this.args.readOnly || this.isTranslating) return false;
    return true;
  }

  get hasErrors(): boolean {
    return this.args.validator.questionOptionErrors(this.args.option).length > 0;
  }

  get isTranslating(): boolean {
    return this.args.selectedLocale !== this.args.defaultLocale;
  }

  @cached
  get localizedOption(): QuestionnaireFieldOption {
    return this.args.option.forLocale(this.args.selectedLocale, this.args.defaultLocale);
  }

  <template>
    <li class="mb-2" data-test-question-option={{@option.position}} ...attributes>
      <div class="flex items-center space-x-4">
        <div class="w-8 text-center">
          <input
            type={{if @isMultipleSelection "checkbox" "radio"}}
            class="inline-block"
            disabled={{not this.canMarkCorrectAnswer}}
            name="{{@inputId}}-correct"
            value={{@option.position}}
            checked={{@option.isCorrect}}
            {{on "change" (fn @setCorrectAnswer @option)}}
            data-test-correct-answer
          />
        </div>
        <div class="flex-auto">
          <TranslatableContent
            @defaultLocale={{@defaultLocale}}
            @selectedLocale={{@selectedLocale}}
            @showLanguagePill={{false}}
          >
            <:original>
              <span
                class="pl-2 text-carbon-50 text-sm"
                data-test-original-language-option-value
              >{{@option.value}}</span>
            </:original>
            <:selected as |isTranslating|>
              <input
                type="text"
                class="inline-block
                  {{if isTranslating '!mb-2' '!m-0'}}
                  {{if @isRTL 'direction-rtl'}}
                  {{if this.hasErrors 'error'}}"
                disabled={{@readOnly}}
                dir={{if @isRTL "rtl"}}
                id="{{@inputId}}-option-{{@option.position}}"
                placeholder="Option"
                value={{this.localizedOption.value}}
                {{on "input" (pick "target.value" (fn @modifyOption this.localizedOption))}}
                {{on "keypress" preventDefaultOnEnter}}
              />
            </:selected>
          </TranslatableContent>
          {{#if this.hasErrors}}
            <label
              for="{{@inputId}}-option-{{@option.position}}"
              class="error-description !text-papaya-50 text-xs"
              data-test-question-option-errors
            >
              {{#each (@validator.questionOptionErrors @option) as |error|}}
                {{error}}<br />
              {{/each}}
            </label>
          {{/if}}
        </div>
        <div class="flex items-center space-x-2">
          {{#if @isDraggable}}
            <div
              {{sortableHandle}}
              class={{if @isDraggable "icon-reSort" "opacity-0 pointer-events-none"}}
              aria-hidden={{if @isDraggable false true}}
              data-question-option-handle
            />
          {{/if}}
          {{#if @showDeleteOptionButton}}
            <button
              type="button"
              class="btn btn--smaller btn--link hover:opacity-75 h-[16px]"
              {{on "click" (fn @deleteOption @option)}}
              data-test-delete-option={{@option.position}}
            >
              {{svgJar "trash-delete-black" height="16" width="16"}}
            </button>
          {{/if}}
        </div>
      </div>
    </li>
  </template>
}
